
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import InternalLayout from '@/components/internal/InternalLayout';
import { Link } from 'react-router-dom';
import { 
  Award, 
  Users, 
  BookOpen, 
  Briefcase,
  Star,
  CheckCircle,
  Clock,
  TrendingUp,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const Academy = () => {
  const certificationTracks = [
    {
      title: 'Front Office Specialist',
      description: 'Master client relations, booking systems, and administrative excellence.',
      duration: '4-6 weeks',
      modules: 8,
      level: 'Beginner',
      icon: Users,
      benefits: ['Job placement assistance', 'Starting salary: $35-40k', 'Career growth path'],
      color: 'from-juv-rose-clay to-juv-terracotta'
    },
    {
      title: 'Treatment Coordinator',
      description: 'Become an expert in treatment planning and client consultation.',
      duration: '6-8 weeks',
      modules: 12,
      level: 'Intermediate',
      icon: BookOpen,
      benefits: ['Advanced certification', 'Starting salary: $45-55k', 'Leadership opportunities'],
      color: 'from-juv-terracotta to-juv-deep-chestnut'
    },
    {
      title: 'Glow Concierge',
      description: 'Deliver premium luxury service and personalized beauty journeys.',
      duration: '8-10 weeks',
      modules: 15,
      level: 'Advanced',
      icon: Star,
      benefits: ['VIP client access', 'Starting salary: $50-65k', 'Commission opportunities'],
      color: 'from-juv-deep-chestnut to-juv-rose-clay'
    },
    {
      title: 'Studio Leadership',
      description: 'Lead teams, manage operations, and drive business growth.',
      duration: '10-12 weeks',
      modules: 20,
      level: 'Expert',
      icon: Briefcase,
      benefits: ['Management track', 'Starting salary: $65-80k', 'Equity participation'],
      color: 'from-juv-terracotta to-juv-soft-blush'
    }
  ];

  const certificationBenefits = [
    {
      title: 'Job Placement',
      description: 'Direct placement in JUV studios nationwide',
      icon: Briefcase
    },
    {
      title: 'Skill Building',
      description: 'Industry-leading training in aesthetic wellness',
      icon: TrendingUp
    },
    {
      title: 'Exclusive Access',
      description: 'Early access to new treatments and technologies',
      icon: Sparkles
    },
    {
      title: 'Career Growth',
      description: 'Clear advancement paths and leadership opportunities',
      icon: Award
    }
  ];

  const successStories = [
    {
      name: 'Maria Rodriguez',
      role: 'Studio Manager',
      track: 'Started as Front Office Specialist',
      quote: 'The JUV Academy transformed my career. I went from entry-level to managing my own studio in 18 months.',
      image: '👩‍💼'
    },
    {
      name: 'James Chen',
      role: 'Senior Coordinator',
      track: 'Treatment Coordinator Certification',
      quote: 'The hands-on training and mentorship prepared me to excel from day one.',
      image: '👨‍⚕️'
    }
  ];

  return (
    <InternalLayout 
      title="NextGen Academy"
      subtitle="Earn Your Certification. Join the Future of Aesthetics."
    >
      <div className="space-y-12">
        {/* Hero Section */}
        <Card className="bg-gradient-to-r from-juv-rose-clay to-juv-terracotta text-white overflow-hidden">
          <CardContent className="p-8 relative">
            <div className="flex items-center justify-between">
              <div className="max-w-2xl">
                <h2 className="text-3xl font-bold font-juv-heading mb-4">
                  Transform Your Career in Aesthetic Wellness
                </h2>
                <p className="text-white/90 text-lg mb-6">
                  Join thousands of professionals who have launched successful careers through our comprehensive certification programs.
                </p>
                <div className="flex items-center space-x-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold">95%</div>
                    <div className="text-sm text-white/80">Job Placement Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">$50k+</div>
                    <div className="text-sm text-white/80">Average Starting Salary</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">4.9/5</div>
                    <div className="text-sm text-white/80">Student Satisfaction</div>
                  </div>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="w-40 h-40 bg-white/20 rounded-full flex items-center justify-center">
                  <Award className="h-20 w-20 text-white" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Certification Tracks */}
        <div className="space-y-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-juv-deep-chestnut font-juv-heading mb-4">
              Choose Your Certification Track
            </h3>
            <p className="text-juv-deep-chestnut/70 max-w-2xl mx-auto">
              Our structured programs are designed to take you from beginner to industry expert with hands-on training and real-world experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {certificationTracks.map((track, index) => {
              const Icon = track.icon;
              return (
                <Card key={index} className="bg-white border-juv-rose-clay/30 hover:shadow-glow transition-all duration-300">
                  <CardHeader>
                    <div className={`w-16 h-16 bg-gradient-to-br ${track.color} rounded-full flex items-center justify-center mb-4`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-juv-deep-chestnut font-juv-heading">{track.title}</CardTitle>
                    <p className="text-juv-deep-chestnut/70">{track.description}</p>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1 text-juv-deep-chestnut/50" />
                          <span className="text-juv-deep-chestnut/70">{track.duration}</span>
                        </div>
                        <div className="flex items-center">
                          <BookOpen className="h-4 w-4 mr-1 text-juv-deep-chestnut/50" />
                          <span className="text-juv-deep-chestnut/70">{track.modules} modules</span>
                        </div>
                      </div>
                      <Badge 
                        variant="secondary" 
                        className="bg-juv-soft-blush text-juv-deep-chestnut"
                      >
                        {track.level}
                      </Badge>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-medium text-juv-deep-chestnut">Certification Benefits:</h4>
                      <ul className="space-y-2">
                        {track.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-center text-sm text-juv-deep-chestnut/70">
                            <CheckCircle className="h-4 w-4 text-juv-terracotta mr-2 flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Button 
                      className="w-full bg-juv-gradient-primary hover:shadow-glow transition-all duration-300"
                    >
                      Enroll Now
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Certification Benefits */}
        <Card className="bg-white border-juv-rose-clay/30">
          <CardHeader>
            <CardTitle className="text-juv-deep-chestnut font-juv-heading text-center">
              Why Choose JUV Academy?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {certificationBenefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="text-center p-6 bg-juv-soft-blush/30 rounded-lg">
                    <div className="w-12 h-12 bg-juv-terracotta rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-juv-deep-chestnut mb-2">{benefit.title}</h4>
                    <p className="text-sm text-juv-deep-chestnut/70">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Success Stories */}
        <div className="space-y-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-juv-deep-chestnut font-juv-heading mb-4">
              Success Stories
            </h3>
            <p className="text-juv-deep-chestnut/70">
              Hear from graduates who've transformed their careers through JUV Academy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {successStories.map((story, index) => (
              <Card key={index} className="bg-gradient-to-br from-juv-soft-blush to-white border-juv-rose-clay/30">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="text-4xl">{story.image}</div>
                    <div>
                      <h4 className="font-semibold text-juv-deep-chestnut">{story.name}</h4>
                      <p className="text-sm text-juv-terracotta font-medium mb-2">{story.role}</p>
                      <p className="text-xs text-juv-deep-chestnut/70 mb-3">{story.track}</p>
                      <blockquote className="text-sm text-juv-deep-chestnut/80 italic">
                        "{story.quote}"
                      </blockquote>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <Card className="bg-gradient-to-r from-juv-terracotta to-juv-deep-chestnut text-white">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold font-juv-heading mb-4">
              Ready to Start Your Journey?
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Join the next cohort and take the first step toward a rewarding career in aesthetic wellness.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                variant="secondary" 
                className="bg-white text-juv-deep-chestnut hover:bg-white/90"
              >
                Schedule a Call
              </Button>
              <Button 
                size="lg"
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-juv-deep-chestnut"
              >
                Download Brochure
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </InternalLayout>
  );
};

export default Academy;
